/**
 * Tooltip — small dark-navy bubble that appears on hover/focus of its
 * trigger. Same family of floating UI as the user-menu popover and command
 * palette: surface card-like, soft shadow… except inverted (dark navy
 * fill, cream text) so it reads clearly against the brand cream
 * backgrounds without fighting the surface cards.
 *
 * Positioning strategy — `position: fixed` with coordinates computed from
 * the trigger's `getBoundingClientRect()`. We deliberately avoid
 * `position: absolute`: the dashboard sits inside `<main overflow-auto>`,
 * which establishes a clipping context that swallows any tooltip
 * overflowing past `main`'s left/right edges (which previously hid
 * tooltips behind the sidebar). Fixed positioning escapes every overflow
 * ancestor and lets the bubble render anywhere on the viewport.
 *
 * Behavior:
 *   • Open on `mouseenter` + `focusin` of the trigger wrapper.
 *   • Close on `mouseleave`, `focusout`, `Escape`, and on any scroll/resize
 *     event (which would otherwise leave the bubble visually stranded).
 *   • Short open delay (180ms) so quick mouse-overs don't flash a bubble.
 *   • Bubble position is clamped to viewport edges with an 8px gutter so
 *     wide hints near the screen edge stay fully visible.
 *
 * Usage:
 *   <Tooltip content="Users active in the past 28 days">
 *     <BrandIcon name="help" size={13} />
 *   </Tooltip>
 *
 *   <Tooltip content="…" side="top">{trigger}</Tooltip>
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cn } from './cn';

const OPEN_DELAY_MS = 180;
const GAP = 8;              // px between trigger and bubble
const VIEWPORT_PADDING = 8; // px gutter when clamping to viewport edges
const MAX_WIDTH = 240;      // bubble line-wrap cap

export default function Tooltip({
  children,
  content,
  side = 'bottom', // 'bottom' | 'top' | 'left' | 'right'
  className,
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const timerRef = useRef(null);
  const wrapperRef = useRef(null);
  const bubbleRef = useRef(null);

  const cancelOpen = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setOpen(false);
    setCoords(null);
  }, []);

  const scheduleOpen = useCallback(() => {
    if (timerRef.current) return;
    timerRef.current = setTimeout(() => {
      setOpen(true);
      timerRef.current = null;
    }, OPEN_DELAY_MS);
  }, []);

  /* Compute viewport coordinates from the trigger + bubble dimensions. We
   * measure both elements so we can clamp to the viewport edges precisely. */
  const place = useCallback(() => {
    const wrapEl = wrapperRef.current;
    const bubEl  = bubbleRef.current;
    if (!wrapEl || !bubEl) return;
    const trigger = wrapEl.getBoundingClientRect();
    const bubble  = bubEl.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let top = 0;
    let left = 0;
    let arrowStyle = {};

    if (side === 'bottom' || side === 'top') {
      const desiredLeft = trigger.left + trigger.width / 2 - bubble.width / 2;
      const minLeft = VIEWPORT_PADDING;
      const maxLeft = vw - bubble.width - VIEWPORT_PADDING;
      left = Math.min(Math.max(desiredLeft, minLeft), maxLeft);
      top = side === 'bottom'
        ? trigger.bottom + GAP
        : trigger.top - GAP - bubble.height;
      // Arrow stays under the trigger's true center even when the bubble
      // got clamped left/right — so it still "points at" the trigger.
      const triggerCenterX = trigger.left + trigger.width / 2;
      const arrowLeft = triggerCenterX - left;
      arrowStyle = side === 'bottom'
        ? { top: -4, left: arrowLeft - 4 }   // pointing up
        : { bottom: -4, left: arrowLeft - 4 }; // pointing down
    } else if (side === 'right' || side === 'left') {
      const desiredTop = trigger.top + trigger.height / 2 - bubble.height / 2;
      const minTop = VIEWPORT_PADDING;
      const maxTop = vh - bubble.height - VIEWPORT_PADDING;
      top = Math.min(Math.max(desiredTop, minTop), maxTop);
      left = side === 'right'
        ? trigger.right + GAP
        : trigger.left - GAP - bubble.width;
      const triggerCenterY = trigger.top + trigger.height / 2;
      const arrowTop = triggerCenterY - top;
      arrowStyle = side === 'right'
        ? { left: -4, top: arrowTop - 4 }
        : { right: -4, top: arrowTop - 4 };
    }

    setCoords({ top, left, arrowStyle });
  }, [side]);

  /* Measure + position when the bubble first mounts and whenever content
   * changes (which may resize it). `useLayoutEffect` so the bubble paints
   * at its final position on the same frame — no visible jump. */
  useLayoutEffect(() => {
    if (open) place();
  }, [open, place, content]);

  /* Close on scroll/resize — re-anchoring the bubble while scrolling tends
   * to look glitchy, and users naturally move the cursor away anyway. */
  useEffect(() => {
    if (!open) return undefined;
    function onMove() { cancelOpen(); }
    function onKey(e) {
      if (e.key === 'Escape') cancelOpen();
    }
    window.addEventListener('scroll', onMove, true); // capture: any scroller
    window.addEventListener('resize', onMove);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, cancelOpen]);

  /* Clean up timer on unmount. */
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  if (!content) return children;

  return (
    <span
      ref={wrapperRef}
      className={cn('relative inline-flex', className)}
      onMouseEnter={scheduleOpen}
      onMouseLeave={cancelOpen}
      onFocus={scheduleOpen}
      onBlur={cancelOpen}
    >
      {children}
      {open && (
        <span
          ref={bubbleRef}
          role="tooltip"
          className={cn(
            'fixed z-[1000] pointer-events-none bg-navy-deep text-warm-white',
            'font-sans rounded-md',
            'animate-[fadeSlideIn_140ms_ease-out]',
          )}
          style={{
            // Hidden offscreen on the first paint so the measurement pass
            // doesn't flash the bubble in the wrong place. `place()` fills
            // in real `top`/`left` on the same frame via useLayoutEffect.
            top: coords ? coords.top : -9999,
            left: coords ? coords.left : -9999,
            width: 'max-content',
            maxWidth: MAX_WIDTH,
            padding: '6px 10px',
            fontSize: 12,
            lineHeight: 1.4,
            letterSpacing: '0.005em',
            whiteSpace: 'normal',
            // Hard-reset inheritable typography so the bubble never picks
            // up uppercase/condensed/bold treatment from its trigger
            // (e.g. eyebrow labels). Even with position:fixed, the bubble
            // is still a DOM descendant of the trigger wrapper, so
            // text-transform / font-weight / font-family inherit.
            textTransform: 'none',
            fontWeight: 500,
            fontStyle: 'normal',
            // Soft drop shadow matching the user-menu popover family.
            boxShadow: '0 8px 24px #2C3E5026, 0 2px 6px #2C3E5012',
          }}
        >
          {/* Arrow — rotated 8×8 square anchored to the trigger's true
           *  center (computed in `place()`) so it points at the icon even
           *  when the bubble's body is clamped to the viewport edge. */}
          {coords && (
            <span
              aria-hidden
              className="absolute bg-navy-deep"
              style={{
                ...coords.arrowStyle,
                width: 8,
                height: 8,
                borderRadius: 1,
                transform: 'rotate(45deg)',
                zIndex: -1,
              }}
            />
          )}
          {content}
        </span>
      )}
    </span>
  );
}
